/**
 * Imperative bridge between the ElevenLabs widget's client tools and the
 * consultation stepper.
 *
 * Why this exists: the widget's client tools are registered inside a plain DOM
 * event handler in `useElevenLabsWidget` (reports module), which has no access
 * to the React `ConsultationContext` / stepper. This module is a tiny
 * module-level singleton — `ConsultationContext` registers a handler that
 * closes over the live stepper, and the `focusDom` client tool (via its
 * virtual `consultation-nav:` targets, see `navigateFromAgentSelector`)
 * calls into it.
 *
 * Navigation runs through the stepper's `goTo`, which writes the `section`/`sub`
 * React-Router search params — a client-side SPA update. It does NOT reload the
 * document, so the widget (and its live ElevenLabs session) survive the jump.
 * The module-level indirection means the tool always reaches the LATEST
 * registered handler, so it never calls a stale stepper closure.
 */

export type NavResult = { ok: true } | { ok: false; error: string };

/**
 * Navigate to `section` (and optional `sub`). `sub` omitted → first available
 * sub-step of the section. Returns whether the target was valid/reachable so
 * the agent gets real feedback instead of a silent no-op.
 */
export type ConsultationNavHandler = (
  section: string,
  sub?: string
) => NavResult;

let handler: ConsultationNavHandler | null = null;

export const setConsultationNavHandler = (
  fn: ConsultationNavHandler | null
): void => {
  handler = fn;
};

export const navigateConsultation = (
  section: string,
  sub?: string
): NavResult => {
  if (!handler) return { ok: false, error: "consultation_not_ready" };
  return handler(section, sub);
};

/**
 * Virtual `focusDom` target for consultation navigation. Instead of a
 * separate client tool, the ElevenLabs agent reuses `focusDom` with a
 * selector of the form:
 *
 *   [data-agent-target="consultation-nav:<section>"]
 *   [data-agent-target="consultation-nav:<section>/<sub>"]
 *
 * e.g. `[data-agent-target="consultation-nav:systems/heart"]`. No DOM
 * element carries this attribute — `focusDom` detects the prefix and
 * routes here, navigating the stepper (a client-side URL-param update,
 * so the widget/session survive) instead of highlighting an element.
 *
 * Returns `null` when the selector is NOT a consultation-nav target, so
 * the caller falls through to its normal DOM focus behaviour. Unknown or
 * hidden sections/subs return `{ ok:false }` WITHOUT navigating.
 */
const NAV_SELECTOR_RE =
  /^\[data-agent-target="consultation-nav:([a-z0-9-]+)(?:\/([a-z0-9-]+))?"\]$/;

export const navigateFromAgentSelector = (
  selector: string
): NavResult | null => {
  const m = NAV_SELECTOR_RE.exec(selector);
  if (!m) return null;
  return navigateConsultation(m[1], m[2]);
};
